'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import {
  useNewsletterSignup,
  useConfetti,
} from '@/helpers/newsletter.helpers';
import useFocusOnMount from '@/hooks/use-focus-on-mount';

import Spinner from '@/components/Spinner';
import ButtonRainbow from '@/components/ButtonRainbow';
import SubscribeGeyser from '@/components/SubscribeGeyser';
import SubscribeError from '@/components/SubscribeError';

import { useSignupContext } from './SignupProvider';
import TextLink from '@/components/Link/TextLink';

interface Props {
  focusOnMount: boolean;
}

const SignupForm = ({ focusOnMount }: Props) => {
  const {
    confettiStatus,
    handleLoadConfetti,
    handleTriggerConfetti,
  } = useConfetti();

  const { handleSuccess } = useSignupContext();

  const inputRef = React.useRef<HTMLInputElement>(null);

  useFocusOnMount(inputRef, { enabled: focusOnMount });

  const {
    status,
    emailFields,
    error,
    numOfEncounteredErrors,
    emailId,
    honeypotElem,
    handleSubmit,
  } = useNewsletterSignup({
    tags: ['primaryNewsletter'],
    formName: 'effectivePortfolio',
    callbacks: {
      onStart: handleLoadConfetti,
      onSuccess: (email) => {
        handleTriggerConfetti();
        handleSuccess(email);
      },
    },
  });

  return (
    <>
      <SubscribeGeyser status={confettiStatus} mode="burst" />

      <Form onSubmit={handleSubmit}>
        {honeypotElem}

        <Label htmlFor={emailId}>Email:</Label>

        <InputWrapper>
          <EmailInput id={emailId} {...emailFields} />
          <SubmitButton disabled={status === 'submitting'}>
            {status === 'submitting' ? (
              <Spinner size={20} />
            ) : (
              'Submit'
            )}
          </SubmitButton>
        </InputWrapper>

        <AfterForm>
          <ErrorMessage
            variant="effective-portfolio"
            status={status}
            error={error}
            numOfEncounteredErrors={numOfEncounteredErrors}
            fallback={
              <Disclaimer>
                You'll also be subscribed to my personal newsletter,
                which contains a bunch of other goodies for
                developers! If you’re not interested, you can{' '}
                <TextLink href="/effective-portfolio/download-book/">
                  jump straight to the download page
                </TextLink>
                .
              </Disclaimer>
            }
          />
        </AfterForm>
      </Form>
    </>
  );
};

const Form = styled.form`
  margin-top: 16px;
`;

const Label = styled.label`
  display: none;
`;

const EmailInput = styled.input`
  display: block;
  flex: 1;
  height: 48px;
  padding-left: 8px;
  border: none;
  background: transparent;
  font-size: 1rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    font-size: 1.125rem;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #fff;
  border-radius: 4px;
  padding: 8px;
  box-shadow:
    -06px 0.6px 1px -7px rgba(0, 0, 0, 0.02),
    -1.4px 1.4px 2.5px -7px rgba(0, 0, 0, 0.028),
    -2.6px 2.6px 4.6px -7px rgba(0, 0, 0, 0.035),
    -5px 4.7px 8.3px -7px rgba(0, 0, 0, 0.042),
    -8px 8.8px 15.5px -7px rgba(0, 0, 0, 0.05),
    -15px 21px 37px -7px rgba(0, 0, 0, 0.07);
`;

const SubmitButton = styled(ButtonRainbow)`
  width: 100px;
  font-size: 1rem;

  && {
    /* Arg busted class orders again! */
    min-height: 3rem;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    width: 125px;
    height: 48px;
    font-size: 1.125rem;
  }
`;

const AfterForm = styled.div`
  /* Necessary to prevent a layout shift when toggling between Disclaimer and ErrorMessage */
  min-height: 3rem;
  margin-top: 24px;
  padding: 0 8px;
`;

const Disclaimer = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  text-align: center;
  opacity: 0.75;
  text-wrap: balance;
`;

const ErrorMessage = styled(SubscribeError)`
  --color-primary: var(--color-error);

  p {
    margin: 0;
    font-size: 0.9375rem;
    text-align: center;
    opacity: 0.75;
    text-wrap: balance;
    color: var(--color-error);
  }
`;

export default SignupForm;
